import { formatOutcomeLabel, pairingForOutcome } from '../forecasting-utils';
import { BudgetOutcome, EfficiencyMetric } from './budgetBuilderContract';

const COMPACT_NUMBER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1
});
const COMPACT_CURRENCY = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 1
});
const CURRENCY_2DP = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2
});

export const EMPTY_VALUE = '--';

/** Spend is always dollars. */
export const formatSpendValue = (val: number | null): string =>
  val == null ? EMPTY_VALUE : COMPACT_CURRENCY.format(val);

/** Volume outcome is a count; revenue outcome is dollars. */
export const formatOutcomeValue = (outcome: BudgetOutcome, val: number | null): string => {
  if (val == null) {
    return EMPTY_VALUE;
  }
  return outcome === 'revenue' ? COMPACT_CURRENCY.format(val) : COMPACT_NUMBER.format(val);
};

/** CPA is a per-unit dollar cost; ROAS is a multiple. */
export const formatEfficiencyValue = (metric: EfficiencyMetric, val: number | null): string => {
  if (val == null) {
    return EMPTY_VALUE;
  }
  return metric === 'CPA' ? CURRENCY_2DP.format(val) : `${val.toFixed(2)}×`;
};

/** Compact efficiency for axis ticks (e.g. "18.3K×" instead of "18285.18×"). */
export const formatEfficiencyCompact = (metric: EfficiencyMetric, val: number | null): string => {
  if (val == null) {
    return EMPTY_VALUE;
  }
  return metric === 'CPA' ? COMPACT_CURRENCY.format(val) : `${COMPACT_NUMBER.format(val)}×`;
};

export const outcomeLabel = (outcome: BudgetOutcome, kpi?: string): string =>
  formatOutcomeLabel(kpi ?? '', pairingForOutcome(outcome));

/**
 * The aggregation grain a forecast group was run at, derived from which
 * dimensions are present (platform/tactic are empty at coarser grains). Lets the
 * UI surface the grain so it's clear tactic (or platform) isn't always available.
 */
export const grainLabel = (channel: string, platform: string, tactic: string): string =>
  [channel && 'Channel', platform && 'Platform', tactic && 'Tactic'].filter(Boolean).join(' × ');
